import { Amount, Unit } from 'uom';
import { Quantity, Units } from 'uom-units';
import { AmountObject } from './amount.model';
import { UOMS } from './units-definition';
import { miner } from '@cbre/digger';

/**
 * Finds the `Unit` for a passed alias in `name`
 * @param name string
 * @returns `Unit.Unit` from `uom`
 */
export const findUnit = (name: string) => {
  const targetName = name?.toLowerCase();
  const uom = Object.values(UOMS).find(({ aliases }) => aliases.some(alias => alias === targetName));
  if (!uom) {
    throw Error(`No valid UOM found for ${name}`);
  }
  return uom.unit;
};

/**
 * Tries to find unit otherwise tries with defaultUnit (by default is SquareFeet)
 * @param name
 * @param defaultUnit
 */
export const getSafeUnit = (name: string, defaultUnit = Units.SquareFeet) => {
  try {
    return findUnit(name);
  } catch (e) {
    return defaultUnit;
  }
};

/**
 * Finds the `Unit` for a passed alias in `name`
 * @param name string
 * @param defaultUnit = null
 * @returns `Unit.Unit` from `uom`
 */
export const getUnit = (name: string, defaultUnit = null) => {
  if (defaultUnit) {
    return getSafeUnit(name, defaultUnit);
  }
  return findUnit(name);
};

/**
 * Returns UOM definitions of `targetType`
 * @param targetType
 */
export const getUnitsOf = (targetType: string) => Object.values(UOMS).filter(({ type }) => type === targetType);

/**
 * Creates an instance of Amount.Amount using the amount value and the targetUom found by getUnit
 * @param value numeric value
 * @param uom string
 */
export const makeAmountWith = ({
  value,
  uom,
  defaultUnit = Units.SquareFeet,
}): Amount.Amount<Quantity.Area | Quantity.Length> => Amount.create(value || 0, getUnit(uom, defaultUnit));

/**
 * Creates an instance of Amount.Amount using the amount value and the targetUom found by getUnit
 * @param value numeric value
 * @param uom string
 */
export const makeAmount = (value: number, uom: string): Amount.Amount<any> => Amount.create(value, getUnit(uom));

/**
 * Creates an `AmountObject` object
 * @param data
 * @param dataset
 * @param defaultUom = "sf" - default uom to use in case absence
 */
export const makeAmountObject = (data, dataset, defaultUom = 'Square Foot'): AmountObject => {
  const { value, uom } = miner(data, dataset);
  return { value: value || 0, uom: uom || defaultUom };
};

/**
 * Converts `value` in specific Amount to its equivalent in `unit`
 * @param unit Unit
 * @param value Amount
 */
export const amountAs = (unit: Unit.Unit<any>, value: Amount.Amount<any>) => Amount.valueAs(unit, value);
